/**
 * 入力途中の下書き（セッション切れ 401 のときだけ、その端末に残す）。
 *
 * 401 を受けると App はログイン画面に切り替わり、開いていたフォームは消える。
 * そこで切り替える直前に、開いているフォームの値を localStorage に書き、
 * ログインし直して同じ画面（URL のハッシュはそのまま）に戻ったときに復元する。
 * 明示的なログアウトでは消す（共有端末に入力を残さない）。復元したら消す。
 *
 * M3: 下書きは利用者ごとに分ける（キーは `scs.draft.<userId>.<key>`）。
 * - 戻すのは、書いたのと同じ利用者がログインし直したときだけ。
 * - 別の利用者がログインしたら、他の人の下書きは全部消す（共有端末で前の人の報告・申請が
 *   次の人のフォームに戻り、次の人の名前で送れてしまうのを防ぐ）。
 * - 起動時に 24 時間を過ぎた下書き（と、利用者の分からない古い形式の下書き）を消す。
 * パスワード・TOTP の欄は下書きの対象にしない（Settings・Login は useDraft を使わない）。
 */
import { useEffect, useRef, useState } from "react";

const PREFIX = "scs.draft.";
const MAX_AGE_MS = 24 * 3600_000;
/** 下書きの書き出し元。ttlMs を持つものは、その期間だけ戻す（自動保存の下書き）。 */
const providers = new Map<string, { get: () => unknown; ttlMs?: number; skip?: () => boolean }>();
/** 今ログインしている利用者。App が /auth/me の結果で設定する（画面を出す前に）。 */
let currentUser: string | null = null;

function storageKey(userId: string, key: string): string {
  return `${PREFIX}${userId}.${key}`;
}

/** 下書きのキーから利用者を取り出す。古い形式（利用者を含まない）なら null。 */
function ownerOf(k: string): string | null {
  const rest = k.slice(PREFIX.length);
  const dot = rest.indexOf(".");
  return dot > 0 ? rest.slice(0, dot) : null;
}

function removeWhere(pred: (k: string, raw: string | null) => boolean): void {
  try {
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const k = localStorage.key(i);
      if (k?.startsWith(PREFIX) && pred(k, localStorage.getItem(k))) localStorage.removeItem(k);
    }
  } catch { /* 読めない環境 */ }
}

function isExpired(raw: string | null): boolean {
  try {
    const o = JSON.parse(raw ?? "") as { at?: unknown; ttl?: unknown };
    // 自動保存の下書きは ttl（保存した期間）を持つ。持たない下書き（401 の直前に書いたもの）は 24 時間
    const ttl = typeof o.ttl === "number" ? o.ttl : MAX_AGE_MS;
    return typeof o.at !== "number" || Date.now() - o.at > ttl;
  } catch { return true; }
}

/** 24 時間を過ぎた下書きと、利用者の分からない古い形式の下書きを消す。起動時に1回呼ぶ。 */
export function purgeExpiredDrafts(): void {
  removeWhere((k, raw) => ownerOf(k) === null || isExpired(raw));
}
purgeExpiredDrafts();

/**
 * ログイン中の利用者を知らせる（/auth/me が返ったら、画面を出す前に呼ぶ）。
 * 別の利用者の下書きはここで全部消す。
 */
export function setDraftUser(userId: string): void {
  currentUser = userId;
  removeWhere((k, raw) => ownerOf(k) !== userId || isExpired(raw));
}

/** 401 の直前に呼ぶ。開いているフォームの値を、今の利用者の下書きとして全部書き出す。 */
export function flushDrafts(): void {
  const userId = currentUser;
  // この後はログインし直すまで、誰の下書きとしても戻さない
  currentUser = null;
  if (!userId) return;
  for (const [key, p] of providers) {
    try {
      if (p.skip?.()) continue;
      localStorage.setItem(storageKey(userId, key), JSON.stringify({ at: Date.now(), ...(p.ttlMs ? { ttl: p.ttlMs } : {}), value: p.get() }));
    } catch { /* 保存できなくても続ける */ }
  }
}

/** ログアウト時に呼ぶ。 */
export function clearAllDrafts(): void {
  currentUser = null;
  removeWhere(() => true);
}

function take<T>(key: string): T | undefined {
  if (!currentUser) return undefined;
  const k = storageKey(currentUser, key);
  try {
    const raw = localStorage.getItem(k);
    if (!raw) return undefined;
    localStorage.removeItem(k);
    // 1日より古い下書きは戻さない（別の作業の残りを誤って出さない）
    if (isExpired(raw)) return undefined;
    return (JSON.parse(raw) as { at: number; value: T }).value;
  } catch { return undefined; }
}

/**
 * フォームの値を下書きの対象にする。マウント時に下書きがあれば restore を1回呼ぶ。
 * key は画面と対象が分かる名前（"requestNew"、`approve:${id}` など）。
 * 戻したときは true を返す（L-06: 「入力の途中だった内容を戻しました」と伝えるため）。
 */
export function useDraft<T>(key: string, value: T, restore: (v: T) => void): boolean {
  const [restored, setRestored] = useState(false);
  const valueRef = useRef(value);
  valueRef.current = value;
  const restoreRef = useRef(restore);
  restoreRef.current = restore;
  useEffect(() => {
    const saved = take<T>(key);
    if (saved !== undefined) { restoreRef.current(saved); setRestored(true); }
    providers.set(key, { get: () => valueRef.current });
    return () => { providers.delete(key); };
  }, [key]);
  return restored;
}

/** 自動保存の下書きを残す期間。年次点検のように、何日かに分けて答える入力のため、401 の下書き（24 時間）より長い。 */
const AUTOSAVE_TTL_MS = 14 * 24 * 3600_000;
/** 入力が止まってから書くまでの間（打鍵のたびに localStorage へ書かない）。 */
const AUTOSAVE_DEBOUNCE_MS = 500;

function peek<T>(key: string): T | undefined {
  if (!currentUser) return undefined;
  try {
    const raw = localStorage.getItem(storageKey(currentUser, key));
    if (!raw || isExpired(raw)) return undefined;
    return (JSON.parse(raw) as { value: T }).value;
  } catch { return undefined; }
}

/**
 * 入力のたびに保存する下書き（useDraft は 401 の直前にしか書かないが、こちらは常に書く）。
 * シートを閉じても、再読み込みしても、セッション切れでログインし直しても、同じ利用者なら戻る。
 * 保存先・利用者ごとの分け方・ログアウトで消す扱いは useDraft と同じ（`scs.draft.<userId>.<key>`）。
 *
 * - マウント時（key が変わったとき）に下書きがあれば restore を1回呼び、restored を true にする。戻しても下書きは消さない
 *   （送るまで残す）。
 * - 値が変わったら debounce して書く。閉じる・タブを離れる・隠すときは待たずに書く。
 *   isEmpty が true の値は書かず、残っていた下書きも消す。
 * - 送れたら finish() で消す（以降は書かない）。「下書きを捨てる」は discard()（フォームを空に戻すのは呼び出し側）。
 */
export function useAutosaveDraft<T>(
  key: string, value: T, opts: { restore: (v: T) => void; isEmpty: (v: T) => boolean },
): { restored: boolean; discard: () => void; finish: () => void } {
  const [restored, setRestored] = useState(false);
  const valueRef = useRef(value);
  valueRef.current = value;
  const optRef = useRef(opts);
  optRef.current = opts;
  const finished = useRef(false);
  // 最初の値（マウント直後の空の値）。これと同じ間は何も書かない（StrictMode の二重実行や、
  // 戻す前に外れたときに、残っている下書きを空の値で消さないため）
  const initial = useRef(value);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const writeNow = useRef(() => {});
  writeNow.current = () => {
    if (timer.current) { clearTimeout(timer.current); timer.current = null; }
    if (finished.current || !currentUser || valueRef.current === initial.current) return;
    const k = storageKey(currentUser, key);
    try {
      if (optRef.current.isEmpty(valueRef.current)) localStorage.removeItem(k);
      else localStorage.setItem(k, JSON.stringify({ at: Date.now(), ttl: AUTOSAVE_TTL_MS, value: valueRef.current }));
    } catch { /* 保存できなくても入力は続けられる */ }
  };

  // 戻す（key ごとに1回）。401 のときの書き出し元にも入れる（debounce 中の分を失わない）
  useEffect(() => {
    finished.current = false;
    const saved = peek<T>(key);
    if (saved !== undefined) { optRef.current.restore(saved); setRestored(true); } else setRestored(false);
    providers.set(key, { get: () => valueRef.current, ttlMs: AUTOSAVE_TTL_MS, skip: () => finished.current || optRef.current.isEmpty(valueRef.current) });
    const flush = () => writeNow.current();
    const onHide = () => { if (document.visibilityState === "hidden") flush(); };
    window.addEventListener("pagehide", flush);
    document.addEventListener("visibilitychange", onHide);
    return () => {
      window.removeEventListener("pagehide", flush);
      document.removeEventListener("visibilitychange", onHide);
      providers.delete(key);
      flush(); // シートを閉じる＝この部品が外れるときに、待っている分を書く
    };
  }, [key]);

  // 値が変わったら debounce して書く
  useEffect(() => {
    if (value === initial.current) return;
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => writeNow.current(), AUTOSAVE_DEBOUNCE_MS);
    return () => { if (timer.current) { clearTimeout(timer.current); timer.current = null; } };
  }, [value]);

  const remove = () => {
    if (timer.current) { clearTimeout(timer.current); timer.current = null; }
    if (!currentUser) return;
    try { localStorage.removeItem(storageKey(currentUser, key)); } catch { /* 読めない環境 */ }
  };
  return {
    restored,
    discard: () => { remove(); setRestored(false); },
    finish: () => { finished.current = true; remove(); },
  };
}
